// Outbound caller ID ("Calling from"). Pure helpers, unit-tested.
//
// The server tells the softphone the user's numbers and whether it can apply a
// choice per call (`ready`: the add-on's rule is loaded in Asterisk). Then a
// call to a phone number is dialled as cpout<number> with the header
// X-CP-CallerID: <number> or "rotate"; the rule checks that the number is
// really the user's, sets it, and hands the call to MagnusBilling as <number>.
// Without `ready`, or for codes like *43, the number is dialled as it is and
// the SIP account's own CallerID applies.

import type { CallerIdInfo } from '../api';

export const OUT_PREFIX = 'cpout';
export const CALLER_ID_HEADER = 'X-CP-CallerID';
export const ROTATE = 'rotate';

/**
 * What JsSIP dials and which extra SIP headers it sends. `serverCalls`: the
 * server places outgoing calls (so they can be transferred) - also dialled as
 * cpout<number>, with no header when there's no number to pick (the SIP
 * account's own CallerID then applies, as with a plain dial).
 */
export function dialTarget(
  number: string,
  info: CallerIdInfo | null | undefined,
  serverCalls = false,
): { target: string; headers: string[] } {
  if (!/^\+?[0-9]/.test(number)) return { target: number, headers: [] };
  const want = info?.mode === 'rotate' ? ROTATE : info?.number;
  if (info?.ready && want) return { target: `${OUT_PREFIX}${number}`, headers: [`${CALLER_ID_HEADER}: ${want}`] };
  if (serverCalls) return { target: `${OUT_PREFIX}${number}`, headers: [] };
  return { target: number, headers: [] };
}

/** The settings after the user picks `choice` (shown at once, before the server confirms). */
export function withChoice(info: CallerIdInfo, choice: string | null): CallerIdInfo {
  if (choice === ROTATE) return { ...info, selected: ROTATE, mode: info.numbers.length > 1 ? 'rotate' : 'number', number: info.numbers.length > 1 ? null : (info.numbers[0] ?? null) };
  if (choice && info.numbers.includes(choice)) return { ...info, selected: choice, mode: 'number', number: choice };
  return { ...info, selected: null };
}

/** Value of the picker: 'rotate' or the number calls show. */
export function pickerValue(info: CallerIdInfo): string {
  return info.mode === 'rotate' ? ROTATE : (info.number ?? '');
}
